'use client';

import { Avatar, Form, controlSizeClassName } from '@statura/ui';
/**
 * Topbar user menu (Phase 3).
 *
 * Replaces the static role chip in `dashboard-shell-v2.tsx` with a real
 * dropdown: avatar / role display, My profile, Settings shortcut, Sign out.
 *
 * Notes vs the prototype (`nav-redesign/shell.jsx:192-245`):
 * - The prototype's "View as" role-switcher is DEMO-ONLY (lets a designer
 *   preview the sidebar under a different role alias). Not portable to
 *   production — would let any user impersonate any role and bypass RBAC.
 *   Omitted here. (SYSTEM_ADMIN role-impersonation could ship later as
 *   nav-v2-vNEXT under a separate, server-gated mechanism.)
 * - The signout target `/auth/signout` is the same one the legacy shell
 *   uses (`dashboard-layout.tsx` passes `signOutHref="/auth/signout"`).
 */

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import {
  Building2,
  ChevronDown,
  CircleHelp,
  LogOut,
  Settings,
  User as UserIcon,
} from 'lucide-react';
import { trackNavV2Event } from '@/lib/nav-v2/telemetry';

interface Props {
  displayName: string;
  initials: string;
  roleName: string;
  /**
   * Whether the current user can reach `/settings`. Computed in
   * `DashboardShellV2` via the shared `navV2ItemIsVisible` predicate so
   * the topbar applies the same gate as the sidebar footer.
   *
   * Codex Phase 3 review HIGH finding: pre-fix this link rendered for
   * every v2 user, but `/settings` is restricted to
   * `TENANT_CONFIGURATION_ALLOWED_ROLES` per `route-access.ts:1168`.
   * The sidebar footer correctly hides Settings for non-admin roles;
   * the topbar must too.
   */
  canViewSettings: boolean;
  onHelp?: () => void;
  /**
   * path-to-go S23 — shell entry for the platform-admin console at `/team`
   * ((admin) route group). SYSTEM_ADMIN only; pages still enforce
   * requirePlatformAdmin + allowlist.
   */
  canViewPlatformConsole?: boolean;
}

export function TopbarUserMenu({
  displayName,
  initials,
  roleName,
  canViewSettings,
  onHelp,
  canViewPlatformConsole = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLAnchorElement | null>(null);

  // Close on outside click + ESC.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Focus first item when opened so arrow / Tab navigation works.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => firstItemRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Phase 3 audit MEDIUM finding: WCAG APG menu pattern requires arrow
  // keys to move focus between menu items. Mirrors the pattern shipped
  // in apps/web/src/components/nav-v2/sidebar/context-menu.tsx.
  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    const activeEl = document.activeElement as HTMLElement | null;
    const currentIdx = items.findIndex((el) => el === activeEl);
    let nextIdx: number | null = null;
    switch (e.key) {
      case 'ArrowDown':
        nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % items.length;
        break;
      case 'ArrowUp':
        nextIdx = currentIdx <= 0 ? items.length - 1 : currentIdx - 1;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = items.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    if (nextIdx !== null) items[nextIdx]?.focus();
  };

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`User menu — ${displayName}, ${roleName}`}
        onClick={() => setOpen((prev) => !prev)}
        className={`${controlSizeClassName('toolbar')} flex w-9 min-w-0 max-w-full shrink-0 xl:w-auto items-center gap-2 whitespace-nowrap rounded-control border border-control-border/30 bg-surface-sheet !p-0 xl:!px-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2`}
        data-nav-v2-user-menu-trigger="true"
      >
        <Avatar
          size="sm"
          fallback={initials}
          aria-hidden="true"
          className="bg-action text-text-inverse"
        />
        <span className="hidden min-w-0 max-w-48 truncate whitespace-nowrap text-caption text-text-body xl:block">
          {roleName}
        </span>
        <ChevronDown
          className="hidden h-3 w-3 shrink-0 text-text-muted xl:block"
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label={`User menu for ${displayName}`}
          onKeyDown={handleMenuKeyDown}
          className="ds-shell-navigation absolute right-0 top-full mt-1.5 z-40 w-56 rounded-overlay border border-control-border/30 bg-surface-sheet p-1.5 shadow-menu"
        >
          <div className="px-2.5 pb-1.5 pt-1">
            <p className="truncate text-body font-semibold text-text-heading">{displayName}</p>
            <p className="truncate text-caption text-text-muted">{roleName}</p>
          </div>
          <div className="my-1 h-px bg-surface-soft" aria-hidden="true" />
          <Link
            ref={firstItemRef}
            role="menuitem"
            href="/me/profile"
            onClick={() => {
              trackNavV2Event('nav_v2_item_clicked', {
                source: 'topbar',
                route: '/me/profile',
                variant: 'user-menu',
              });
              setOpen(false);
            }}
            className="flex items-center gap-2.5 rounded-control px-2.5 py-2 text-body text-text-heading hover:bg-surface-canvas focus:bg-surface-canvas focus:outline-none"
          >
            <UserIcon className="h-3.5 w-3.5 text-selected-foreground" aria-hidden="true" />
            My profile
          </Link>
          {onHelp && (
            <button
              type="button"
              role="menuitem"
              className="flex min-h-11 w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-left text-body text-text-heading hover:bg-surface-canvas focus:bg-surface-canvas focus:outline-none sm:hidden"
              onClick={() => {
                setOpen(false);
                onHelp();
              }}
            >
              <CircleHelp className="size-4" aria-hidden="true" />
              Help with this page
            </button>
          )}
          {canViewSettings && (
            <Link
              role="menuitem"
              href="/settings"
              onClick={() => {
                trackNavV2Event('nav_v2_item_clicked', {
                  source: 'topbar',
                  route: '/settings',
                  variant: 'user-menu',
                });
                setOpen(false);
              }}
              className="flex items-center gap-2.5 rounded-control px-2.5 py-2 text-body text-text-heading hover:bg-surface-canvas focus:bg-surface-canvas focus:outline-none"
              data-nav-v2-user-menu-settings="true"
            >
              <Settings className="h-3.5 w-3.5 text-selected-foreground" aria-hidden="true" />
              Settings
            </Link>
          )}
          {canViewPlatformConsole && (
            <Link
              role="menuitem"
              href="/team"
              onClick={() => {
                trackNavV2Event('nav_v2_item_clicked', {
                  source: 'topbar',
                  route: '/team',
                  variant: 'user-menu',
                });
                setOpen(false);
              }}
              className="flex items-center gap-2.5 rounded-control px-2.5 py-2 text-body text-text-heading hover:bg-surface-canvas focus:bg-surface-canvas focus:outline-none"
              data-nav-v2-user-menu-platform-console="true"
            >
              <Building2 className="h-3.5 w-3.5 text-selected-foreground" aria-hidden="true" />
              Platform console
            </Link>
          )}
          <div className="my-1 h-px bg-surface-soft" aria-hidden="true" />
          {/*
            Phase 3 retro audit P1: `/auth/signout` route exports POST
            only. A <Link> here issued GET → 405 → session never ended.
            Mirror the legacy shell's <Form method="POST"> pattern
            (packages/ui/src/layouts/dashboard-layout.tsx:2616).

            Codex retro pass 2: the original fix here called
            `setOpen(false)` in the button's onClick — which ran BEFORE
            the native submit, unmounted the <Form>, and Chromium then
            cancelled submission ("form is not connected"). Don't close
            the menu on click; the browser navigates away on submit and
            the next render disposes of the menu naturally.
          */}
          <Form action="/auth/signout" method="POST" className="contents">
            <button
              type="submit"
              role="menuitem"
              className="flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-left text-body text-text-body hover:bg-surface-canvas focus:bg-surface-canvas focus:outline-none"
              data-nav-v2-signout="true"
            >
              <LogOut className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
              Sign out
            </button>
          </Form>
        </div>
      )}
    </div>
  );
}
